import React, { useEffect, useMemo, useState } from 'react'

import { Header } from 'components'
import { useGetCheckinData } from 'hooks/index'
import { TAlert, TSection, TSectionsNames, TTagging, TCheckinWidgetSectionItems } from 'interface'
import ErrorView from 'components/QueryBoundaries/ErrorView'
import { useApplicationCtx } from 'context/application'
import { tagEvent } from 'utils/tagging'
import { detectBrokenSequence } from 'utils'
import { BannerSkeleton } from 'components/BannerSkeleton'

import { interWbSession } from '@interco/inter-webview-bridge'
import { BottomSheet } from '@interco/inter-ui/components/BottomSheet'
import { CarouselBanner } from '@interco/citrus-ads'

import { BrokenSequence } from './Sections/BrokenSequence'
import * as S from './styles'
import Checkin from './Sections/Checkin'

type TAppApplication = 'PF' | 'PJ'

function Home() {
  const { data, isRefetching, isLoading } = useGetCheckinData()
  const { showBottomSheet, setShowBottomSheet } = useApplicationCtx()
  const [isLoadingAppInfo, setIsLoadingAppInfo] = useState(false)
  const [app, setApp] = useState<TAppApplication>()

  useEffect(() => {
    const init = async () => {
      setIsLoadingAppInfo(true)
      const result = await interWbSession.getAppInfo()
      setApp(result.application)
      setIsLoadingAppInfo(false)
    }

    tagEvent({
      type: 'CHECKIN_ACESSO',
      content_action: 'Dado carregado',
    } as TTagging)
    init()
  }, [])

  const SectionsList = useMemo(() => {
    const sectionsList = {} as Record<TSectionsNames, TSection>
    data?.sections.forEach((section: TSection) => {
      sectionsList[section.id] = section
    })
    return sectionsList
  }, [data])

  const isSequenceBroken = useMemo(() => {
    const checkinSection = SectionsList?.LOYALTY_DAILY_CHECK_IN_WIDGET
    if (!checkinSection?.content?.[0]?.sectionItems) return false

    const checkinItems = checkinSection.content[0].sectionItems as TCheckinWidgetSectionItems[]
    if (!checkinItems?.[0]?.checkIns) return false

    return detectBrokenSequence(checkinItems[0].checkIns)
  }, [SectionsList])

  if (SectionsList?.LOYALTY_DAILY_CHECK_IN_GET_ERROR_WIDGET) {
    return (
      <S.Wrapper>
        <ErrorView
          headerTitle={data?.title}
          {...(SectionsList?.LOYALTY_DAILY_CHECK_IN_GET_ERROR_WIDGET.content[0]
            .sectionItems[0] as TAlert)}
        />
      </S.Wrapper>
    )
  }

  if (!isLoading && !isRefetching && data && !data.isCampaignActive) {
    return (
      <S.Wrapper>
        <ErrorView
          headerTitle={data?.title || 'Campanha indisponível'}
          title="Houve um erro por aqui"
          description="No momento, não é possível acessar essa página. Você pode tentar novamente mais tarde."
        />
      </S.Wrapper>
    )
  }

  return (
    <S.Wrapper>
      <Header title={data?.title || 'Check-in premiado'} />

      <S.Sections>
        {isSequenceBroken ? (
          <BrokenSequence />
        ) : (
          <Checkin
            section={SectionsList?.LOYALTY_DAILY_CHECK_IN_WIDGET}
            isRefetching={isRefetching || isLoadingAppInfo}
          />
        )}

        {SectionsList?.LOYALTY_DAILY_CHECK_IN_DAILY_NEWS_WIDGET && <S.Divider />}

        {isLoading || isLoadingAppInfo ? (
          <BannerSkeleton />
        ) : (
          <CarouselBanner
            slotKey={app === 'PJ' ? 'HOME_CHECKIN_PREMIADO_PJ' : 'HOME_CHECKIN_PREMIADO'}
          />
        )}
      </S.Sections>

      {showBottomSheet && (
        <BottomSheet onClose={() => setShowBottomSheet(false)}>
          <ErrorView
            bottomSheetMode
            {...(SectionsList?.LOYALTY_DAILY_CHECK_IN_ERROR_WIDGET.content[0]
              .sectionItems[0] as TAlert)}
          />
        </BottomSheet>
      )}
    </S.Wrapper>
  )
}

export default Home
